import { inject, Injectable } from '@angular/core';
import { UuidService } from '../../services/uuid.service';
import { AnimalWeighingSaveInfoViewModel } from '../../viewmodels/shared/animal-weighing-save-info.viewmodel';
import { BatchWeighingStoreCommand } from '../../services/data-store/batch-weighing-store-command';
import { StoreBatchWeighingSaveInfo } from '../../storage/data-store/model/store-batch-weighing-save-info';
import { DataStoreReaderService } from '../../services/data-store/viewmodels/data-store-reader-viewmodels.service';
import { UserService } from '../../services/user.service';
import { mapToStoreAnimalWeighingSaveInfo } from '../../functions/data-store/viewmodels-to-data-store.function';
import { RemovalSaveInfoViewModel } from '../../viewmodels/shared/removal-save-info.viewmodel';
import { StoreRemoval } from '../../storage/data-store/model/registrations/store-removal';
import { TimeService } from '../../services/time.service';
import { StoreUpdateInfo } from '../../storage/store-update-info';
import { UserInfo } from '../../domain/model/user-info';

@Injectable({
  providedIn: 'root',
})
export class ViewModelsToDataStoreMapper {
  private readonly uuidService = inject(UuidService);
  private readonly userInfoService = inject(UserService);
  private readonly dataStoreReaderService = inject(DataStoreReaderService);
  private readonly timeService = inject(TimeService);

  constructor() {}

  mapToBatchWeighingSaveInfo(
    animalWeighingSaveInfos: AnimalWeighingSaveInfoViewModel[],
    batchWeighingStoreCommand: BatchWeighingStoreCommand | undefined
  ): StoreBatchWeighingSaveInfo {
    if (batchWeighingStoreCommand === undefined) {
      throw new Error('batchWeighingStoreCommand is undefined');
    }
    if (animalWeighingSaveInfos.length === 0) {
      throw new Error('AnimalWeighingSaveInfo is empty');
    }

    let batchWeighingId = batchWeighingStoreCommand?.batchWeighingId;

    if (batchWeighingId === undefined) {
      batchWeighingId = animalWeighingSaveInfos[0]!.batchWeighingId;
    }
    if (batchWeighingId === undefined) {
      throw new Error('Batch weighing ID is required, but could not be determined from the provided data.');
    }
    const batchWeighingViewModel = this.dataStoreReaderService.getBatchWeighingViewModel(batchWeighingId);

    const mappedAnimalWeighingSaveInfos =
      animalWeighingSaveInfos.length > 0 ? mapToStoreAnimalWeighingSaveInfo(animalWeighingSaveInfos) : [];

    return new StoreBatchWeighingSaveInfo(
      batchWeighingId,
      batchWeighingViewModel.batchId,
      batchWeighingViewModel.sectionId,
      mappedAnimalWeighingSaveInfos,
      batchWeighingStoreCommand,
      this.userInfoService.getUserInfo()
    );
  }

  createUpdatedRemovals(removalSaveInfos: RemovalSaveInfoViewModel[]): Partial<StoreRemoval>[] {
    return removalSaveInfos.map((item) => ({
      id: item.id.asString(),
      date: item.removal.date,
      pen: item.removal.pen,
      numberOfAnimals: item.removal.numberOfAnimals,
      weight: item.removal.weight,
      code: item.removal.code,
      reason: item.removal.reason,
      updateInfo: new StoreUpdateInfo(
        null,
        this.timeService.getNow(),
        new UserInfo(this.userInfoService.getUser().userId, this.userInfoService.getUserInfo().name)
      ),
    }));
  }
}
